import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  FormControlLabel,
  Checkbox,
  TextField,
  Radio,
  RadioGroup,
  Button,
  Grid
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const GeneralSettings = () => {
  const [formData, setFormData] = useState({
    enableCareerSite: true,
    googleRecaptcha: false,
    jobAlert: false,
    companyName: '',
    companyWebsite: '',
    companyLogo: null,
    aboutCompany: '',
    legalTerms: '',
    email: false,
    phone: false,
    gender: false,
    totalExperience: false,
    currentLocation: false,
    currentCTC: false,
    expectedCTC: false,
    noticePeriod: false,
    applicationSource: false,
    status: false,
    coverLetter: false,
    duplicateJobDays: '180',
    offerLetterDays: '0',
    backgroundType: 'image',
    backgroundImage: null,
    backgroundColor: '#ffffff'
  });

  const handleChange = (name) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setFormData({ ...formData, backgroundImage: file });
    }
  };

  const handleColorChange = (event) => {
    setFormData({ ...formData, backgroundColor: event.target.value });
  };

  return (
    <Box sx={{ 
      backgroundColor: '#ffffff',
      color: '#333333',
      p: 3,
      '& .MuiTypography-root': {
        color: '#333333'
      },
      '& .MuiFormControlLabel-root': {
        color: '#333333'
      },
      '& .MuiInputLabel-root': {
        color: '#666666',
        backgroundColor: '#ffffff',
        padding: '0 4px',
      },
      '& .MuiInputLabel-shrink': {
        transform: 'translate(14px, -9px) scale(0.75)',
        backgroundColor: '#ffffff',
      }
    }}>
      <Paper elevation={3} sx={{ 
        p: 3, 
        mb: 3,
        backgroundColor: '#ffffff',
        border: '1px solid #e0e0e0'
      }}>
        {/* Top Checkboxes */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Checkbox 
                checked={formData.enableCareerSite} 
                onChange={handleChange('enableCareerSite')}
                sx={{
                  color: '#666666',
                  '&.Mui-checked': {
                    color: '#1976d2',
                  },
                }}
              />}
              label="Enable Career Site"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Checkbox 
                checked={formData.googleRecaptcha} 
                onChange={handleChange('googleRecaptcha')}
                sx={{
                  color: '#666666',
                  '&.Mui-checked': {
                    color: '#1976d2',
                  },
                }}
              />}
              label="Google Recaptcha"
            />
          </Grid>
          <Grid item xs={4}>
            <FormControlLabel
              control={<Checkbox 
                checked={formData.jobAlert} 
                onChange={handleChange('jobAlert')}
                sx={{
                  color: '#666666',
                  '&.Mui-checked': {
                    color: '#1976d2',
                  },
                }}
              />}
              label="Job Alert"
            />
          </Grid>
        </Grid>

        {/* Company Information */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Company Name"
              placeholder="Enter company name"
              value={formData.companyName}
              onChange={handleChange('companyName')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiInputLabel-root': {
                  backgroundColor: '#ffffff',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Company Website"
              placeholder="Enter company website URL"
              value={formData.companyWebsite}
              onChange={handleChange('companyWebsite')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiInputLabel-root': {
                  backgroundColor: '#ffffff',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              type="file"
              label="Company Logo"
              placeholder="Choose logo file"
              InputLabelProps={{ 
                shrink: true,
                sx: {
                  backgroundColor: '#ffffff',
                  padding: '0 4px',
                }
              }}
              onChange={handleChange('companyLogo')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                }
              }}
            />
          </Grid>
        </Grid>

        {/* Rich Text Editors */}
        <Typography variant="h6" sx={{ color: '#333333', fontWeight: 600, mb: 2 }}>
          About Company
        </Typography>
        <Box sx={{ mb: 3 }}>
          <ReactQuill
            theme="snow"
            value={formData.aboutCompany}
            onChange={(value) => setFormData({ ...formData, aboutCompany: value })}
            style={{ 
              backgroundColor: '#ffffff',
              color: '#333333',
              minHeight: '150px',
              marginBottom: '20px',
              border: '1px solid #e0e0e0'
            }}
          />
        </Box>

        <Typography variant="h6" sx={{ color: '#333333', fontWeight: 600, mb: 2 }}>
          Legal Term Text
        </Typography>
        <Box sx={{ mb: 3 }}>
          <ReactQuill
            theme="snow"
            value={formData.legalTerms}
            onChange={(value) => setFormData({ ...formData, legalTerms: value })}
            style={{ 
              backgroundColor: '#ffffff',
              color: '#333333',
              minHeight: '150px',
              marginBottom: '20px',
              border: '1px solid #e0e0e0'
            }}
          />
        </Box>

        {/* Quick Add Form Settings */}
        <Typography variant="h6" sx={{ color: '#333333', fontWeight: 600, mb: 1 }}>
          Quick Add Form Settings
        </Typography>
        <Typography variant="subtitle1" sx={{ color: '#666666', mb: 2 }}>
          Fields to be shown in Job Application Quick Add Form:
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { name: 'email', label: 'Email' },
            { name: 'phone', label: 'Phone' },
            { name: 'gender', label: 'Gender' },
            { name: 'totalExperience', label: 'Total Experience' },
            { name: 'currentLocation', label: 'Current Location' },
            { name: 'currentCTC', label: 'Current CTC' },
            { name: 'expectedCTC', label: 'Expected CTC' },
            { name: 'noticePeriod', label: 'Notice Period' },
            { name: 'applicationSource', label: 'Application Source' },
            { name: 'status', label: 'Status' },
            { name: 'coverLetter', label: 'Cover Letter' }
          ].map((field) => (
            <Grid item xs={12} sm={6} md={3} key={field.name}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData[field.name]}
                    onChange={handleChange(field.name)}
                    sx={{
                      color: '#666666',
                      '&.Mui-checked': {
                        color: '#1976d2',
                      },
                    }}
                  />
                }
                label={field.label}
              />
            </Grid>
          ))}
        </Grid>

        {/* Duration Inputs */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Duplicate Job Application Restriction"
              placeholder="Enter number of days"
              type="number"
              value={formData.duplicateJobDays}
              onChange={handleChange('duplicateJobDays')}
              InputProps={{
                endAdornment: <Typography variant="body2" sx={{ ml: 1, color: '#666666' }}>days</Typography>
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiInputLabel-root': {
                  backgroundColor: '#ffffff',
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Offer Letter Reminder To Candidate"
              placeholder="Enter number of days"
              type="number"
              value={formData.offerLetterDays}
              onChange={handleChange('offerLetterDays')}
              InputProps={{
                endAdornment: <Typography variant="body2" sx={{ ml: 1, color: '#666666' }}>days</Typography>
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                },
                '& .MuiInputLabel-root': {
                  backgroundColor: '#ffffff',
                }
              }}
            />
          </Grid>
        </Grid>

        {/* Background Selection */}
        <RadioGroup
          row
          value={formData.backgroundType}
          onChange={handleChange('backgroundType')}
          sx={{ mb: 2 }}
        >
          <FormControlLabel 
            value="image" 
            control={<Radio sx={{
              color: '#666666',
              '&.Mui-checked': {
                color: '#1976d2',
              },
            }} />} 
            label="Background Image" 
          />
          <FormControlLabel 
            value="color" 
            control={<Radio sx={{
              color: '#666666',
              '&.Mui-checked': {
                color: '#1976d2',
              },
            }} />} 
            label="Background Color" 
          />
        </RadioGroup>

        {formData.backgroundType === 'image' ? (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666666', mb: 1 }}>
              Background Image (Idle Size 1120 * 200)
            </Typography>
            <TextField
              fullWidth
              type="file"
              label="Background Image"
              placeholder="Choose background image"
              InputLabelProps={{ 
                shrink: true,
                sx: {
                  backgroundColor: '#ffffff',
                  padding: '0 4px',
                }
              }}
              onChange={handleImageUpload}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: '#e0e0e0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#1976d2',
                  },
                }
              }}
            />
          </Box>
        ) : (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ color: '#666666', mb: 1 }}>
              Choose Background Color
            </Typography>
            <input
              type="color"
              value={formData.backgroundColor}
              onChange={handleColorChange}
              style={{
                width: '30%',
                height: '40px',
                padding: '0',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            />
          </Box>
        )}

        {/* Save Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={() => console.log('Save clicked', formData)}
          sx={{ 
            mt: 2,
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          Save
        </Button>
      </Paper>
    </Box>
  );
};

export default GeneralSettings;